import type { ReactElement } from 'react';
import { Bot, X, Undo2 } from 'lucide-react';
import type {
  NetworkConnectionListItem,
  NetworkConnectionStatus,
} from '@kirin/api-client/contracts/shadow-ai';
import { confidenceLabelLabels } from '@kirin/api-client/contracts/shadow-ai';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Status pill + (for pending rows) the AI-confidence hint. */
export function NetworkStatusBadge({
  connection,
}: {
  connection: NetworkConnectionListItem;
}): ReactElement {
  const { status, ignoreSource, identificationSource, confidence } = connection;
  const variants: Record<NetworkConnectionStatus, string> = {
    approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    flagged: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    pending: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    ignored: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };

  const label = ((): string => {
    if (status === 'approved' || status === 'flagged') {
      return identificationSource === 'global'
        ? 'AI Agent (builtin)'
        : 'AI Agent';
    }
    if (status === 'ignored') {
      return ignoreSource === 'global' ? 'Dismissed (builtin)' : 'Dismissed';
    }
    return 'Unreviewed';
  })();

  return (
    <div className="flex items-center gap-2">
      <Badge className={cn('text-xs', variants[status])} variant="secondary">
        {label}
      </Badge>
      {status === 'pending' && confidence && (
        <span
          className={cn(
            'text-xs',
            confidence.label === 'likely_ai'
              ? 'text-green-600'
              : confidence.label === 'possible_ai'
                ? 'text-amber-600'
                : 'text-muted-foreground'
          )}
        >
          {confidenceLabelLabels[confidence.label]}
        </span>
      )}
    </div>
  );
}

/** Per-row action callbacks + pending flags for the trailing Actions column. */
export interface NetworkDomainsActions {
  onIdentify: (connection: NetworkConnectionListItem) => void;
  onDismiss: (domain: string) => void;
  onRevertIgnore: (domain: string) => void;
  onRevertClassified: (domain: string) => void;
  isClassifying: boolean;
  isReverting: boolean;
}

/** Identify / Dismiss (pending) or Revert (classified / dismissed) buttons. */
export function NetworkActionsCell({
  row,
  actions,
}: {
  row: NetworkConnectionListItem;
  actions: NetworkDomainsActions;
}): ReactElement {
  const canRevert =
    ((row.status === 'approved' || row.status === 'flagged') &&
      row.identificationSource !== 'global') ||
    (row.status === 'ignored' && row.ignoreSource === 'org');

  if (row.status === 'pending') {
    return (
      <div className="flex items-center justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-950"
          onClick={() => actions.onIdentify(row)}
          disabled={actions.isClassifying}
          title="Identify as AI Agent"
        >
          <Bot className="h-4 w-4 mr-1" />
          Identify
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-muted-foreground hover:text-foreground"
          onClick={() => actions.onDismiss(row.domain)}
          disabled={actions.isClassifying}
          title="Dismiss (not AI)"
        >
          <X className="h-4 w-4 mr-1" />
          Dismiss
        </Button>
      </div>
    );
  }

  if (canRevert) {
    return (
      <div className="flex items-center justify-end">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={() =>
            row.status === 'ignored'
              ? actions.onRevertIgnore(row.domain)
              : actions.onRevertClassified(row.domain)
          }
          disabled={actions.isReverting}
          title="Revert to unreviewed"
        >
          <Undo2 className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return <span className="text-muted-foreground">—</span>;
}
